import { flexRender, Header, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { IndeterminateCheckbox } from './indeterminate-checkbox';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { DataTableRow, ColumnMeta } from '../types';
import { pinnedCellClasses } from '../lib/pinning';

interface TableHeaderProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  hasActions?: boolean;
  pinActions?: boolean;
}

export function DataTableHeader<T extends DataTableRow>({
  table,
  selectable = false,
  hasActions = false,
  pinActions = false
}: TableHeaderProps<T>) {
  const { t } = useTranslation();

  const hasPinnedLeftColumn = table
    .getVisibleLeafColumns()
    .some((column) => (column.columnDef.meta as ColumnMeta)?.pinned === 'left');

  return (
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {selectable && (
            <TableHead
              className={cn(
                'w-12',
                // The checkbox column sits left of a pinned column — it must stick too or it scrolls away underneath
                hasPinnedLeftColumn && pinnedCellClasses('left', { header: true, edge: false })
              )}>
              <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                aria-label={t('common.selectAll')}
              />
            </TableHead>
          )}
          {headerGroup.headers.map((header) => (
            <TableHeaderCell key={header.id} header={header} selectable={selectable} />
          ))}
          {hasActions && (
            <TableHead className={cn('w-[8%]', pinActions && pinnedCellClasses('right', { header: true }))}>
              {t('common.actions')}
            </TableHead>
          )}
        </TableRow>
      ))}
    </TableHeader>
  );
}

function TableHeaderCell<T>({ header, selectable = false }: { header: Header<T, unknown>; selectable?: boolean }) {
  const column = header.column;
  const canSort = column.getCanSort();
  const isSorted = column.getIsSorted();
  const { width, pinned } = (column.columnDef.meta as ColumnMeta | undefined) ?? {};

  return (
    <TableHead
      className={cn(
        width,
        canSort && 'cursor-pointer hover:bg-muted/50 transition-colors',
        pinnedCellClasses(pinned, { header: true, afterCheckbox: selectable })
      )}
      onClick={canSort ? column.getToggleSortingHandler() : undefined}>
      <div className="flex items-center gap-1">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {isSorted && (isSorted === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </div>
    </TableHead>
  );
}
